import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import stylez from "./UpdateOrder.module.css";
import SuccessDialogue from "../../../Components/Shared/SuccessDialog/SuccessDialog";
import axiosInstance from "../../../Configuration/axiosInstance";
import { FaTrash, FaUpload, FaEye } from "react-icons/fa";
import LoadingSpinner from "../../../Components/Shared/Loader/Loader";

const UpdateOrder = () => {
  const location = useLocation();
  const orderData = location.state;

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [custId, setCustID] = useState(orderData?.customer?._id || "");
  const [prodID, setProdId] = useState({});
  const [products, setProducts] = useState([]);
  const [quantity, setQuantity] = useState("");
  const [date, setDate] = useState(
    orderData?.date ? new Date(orderData.date).toISOString().split("T")[0] : ""
  );
  const [productArr, setProductArr] = useState(orderData?.products || []);
  const [msg, setMsg] = useState("");
  const [scheduled, setScheduled] = useState(orderData?.scheduled || false);
  const [isPickup, setIsPickup] = useState(orderData?.isPickup || false);
  const [comment, setComment] = useState(orderData?.comments || "");
  const [orderStatus, setOrderStatus] = useState(orderData?.status || "Pending");
  const [paymentType, setPaymentType] = useState(orderData?.paymentType || "");
  const [paymentCollected, setPaymentCollected] = useState(orderData?.paymentCollected || false);
  const [dialogBox, setDialogBox] = useState(false);
  const [isError, setIsError] = useState(false);
  const [poc, setPoc] = useState(orderData?.purchaseOrdeNo || "");

  // ✅ NEW: Photo upload states
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoPreview, setPhotoPreview] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState(orderData?.photos || []);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);

  // ✅ Define order status options
  const statusOptions = [
    { value: "Pending", label: "Pending", color: "#ffc107" },
    { value: "Assigned", label: "Assigned", color: "#17a2b8" },
    { value: "Completed", label: "Completed", color: "#28a745" },
    { value: "Cancelled", label: "Cancelled", color: "#dc3545" },
  ];

  // ✅ Payment type options
  const paymentTypeOptions = [
    { value: "Cash", label: "Cash" },
    { value: "Card", label: "Card" },
    { value: "EFT", label: "EFT" },
  ];

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [customersResponse, productsResponse] = await Promise.all([
          axiosInstance.get(`customers/allcustomers`),
          axiosInstance.get(`products/allproducts`),
        ]);
        setCustomers(customersResponse.data.data);
        setProducts(productsResponse.data.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  // ✅ NEW: Handle photo selection
  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedPhotos([...selectedPhotos, ...files]);
    
    // Create preview URLs for new photos
    const newPreviews = files.map(file => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      isNew: true,
      size: (file.size / 1024 / 1024).toFixed(2) + ' MB'
    }));
    
    setPhotoPreview([...photoPreview, ...newPreviews]);
  };

  // ✅ NEW: Remove new photo from selection
  const removeNewPhoto = (index) => {
    const newPhotos = selectedPhotos.filter((_, i) => i !== index);
    const newPreviews = photoPreview.filter((_, i) => i !== index);
    
    // Revoke the URL to prevent memory leaks
    if (photoPreview[index]) {
      URL.revokeObjectURL(photoPreview[index].url);
    }
    
    setSelectedPhotos(newPhotos);
    setPhotoPreview(newPreviews);
  };

  // ✅ NEW: Photo Gallery Modal Component
  const PhotoGalleryModal = () => (
    <div className={stylez.photoModalOverlay} onClick={() => setShowPhotoGallery(false)}>
      <div className={stylez.photoModal} onClick={(e) => e.stopPropagation()}>
        <div className={stylez.photoModalHeader}>
          <h3>📸 Order Photos ({existingPhotos.length + photoPreview.length})</h3>
          <button 
            className={stylez.closePhotoModal} 
            onClick={() => setShowPhotoGallery(false)}
          >
            ×
          </button>
        </div>
        <div className={stylez.photoModalContent}>
          {/* Existing Photos */}
          {existingPhotos.length > 0 && (
            <>
              <h4>Current Photos ({existingPhotos.length})</h4>
              <div className={stylez.photoModalGrid}>
                {existingPhotos.map((photoUrl, index) => (
                  <div key={index} className={stylez.photoModalItem}>
                    <img src={photoUrl} alt={`Order photo ${index + 1}`} />
                  </div>
                ))}
              </div>
            </>
          )}
          
          {/* New Photos to Upload */}
          {photoPreview.length > 0 && (
            <>
              <h4>New Photos to Upload ({photoPreview.length})</h4>
              <div className={stylez.photoModalGrid}>
                {photoPreview.map((preview, index) => (
                  <div key={index} className={stylez.photoModalItem}>
                    <img src={preview.url} alt={`New photo ${index + 1}`} />
                    <div className={stylez.newPhotoLabel}>NEW</div>
                  </div>
                ))}
              </div>
            </>
          )}
          
          {existingPhotos.length === 0 && photoPreview.length === 0 && (
            <div className={stylez.noPhotos}>
              📷 No photos for this order yet
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ✅ Enhanced styles for status select
  const getStatusSelectStyles = (baseStyles) => ({
    ...baseStyles,
    control: (provided, state) => ({
      ...provided,
      padding: '0.25rem',
      borderRadius: '4px',
      border: state.isFocused ? '2px solid #007bff' : '1px solid #ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
      fontSize: '1rem',
      minHeight: '48px',
      '&:hover': {
        border: state.isFocused ? '2px solid #007bff' : '1px solid #adb5bd'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? state.data.color 
        : state.isFocused 
        ? '#f8f9fa' 
        : 'white',
      color: state.isSelected ? 'white' : '#495057',
      padding: '12px 16px',
      fontSize: '1rem',
      fontWeight: state.isSelected ? '600' : '400',
      '&:hover': {
        backgroundColor: state.isSelected ? state.data.color : '#e9ecef'
      }
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: state.data.color,
      fontWeight: '600'
    }),
  });

  // ✅ Custom styles for react-select
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      padding: '0.25rem',
      borderRadius: '4px',
      border: state.isFocused ? '2px solid #007bff' : '1px solid #ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
      fontSize: '1rem',
      minHeight: '48px',
      '&:hover': {
        border: state.isFocused ? '2px solid #007bff' : '1px solid #adb5bd'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
      fontWeight: '400'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#495057',
      fontWeight: '500'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#007bff' 
        : state.isFocused 
        ? '#f8f9fa' 
        : 'white',
      color: state.isSelected ? 'white' : '#495057',
      padding: '12px 16px',
      fontSize: '1rem',
      '&:hover': {
        backgroundColor: state.isSelected ? '#007bff' : '#e9ecef'
      }
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '4px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e9ecef'
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '200px',
      padding: '4px'
    })
  };

  const customerOptions = customers?.map((i) => ({
    value: i._id,
    label: i.businessName,
  }));

  const productOptions = products?.map((i) => ({
    value: i._id,
    label: `${i.productName}-${i.sku}`,
  }));

  const customerHandler = (options) => {
    setCustID(options.value);
  };

  const productHandler = (options) => {
    const { value } = options;
    setProdId({ label: value });
  };

  // ✅ Handle status change
  const handleStatusChange = (selectedOption) => {
    const newStatus = selectedOption.value;
    setOrderStatus(newStatus);
    
    // If changing to completed, show a warning
    if (newStatus === "Completed" && orderData?.status !== "Completed") {
      console.log("⚠️ Order will be marked as completed - stock will be reduced");
    }
  };

  // ✅ Handle payment type change
  const handlePaymentTypeChange = (selectedOption) => {
    setPaymentType(selectedOption.value);
  };

  const showError = (errorMessage) => {
    setIsError(true);
    setMsg(errorMessage);
    setDialogBox(true);
    setTimeout(() => {
      setDialogBox(false);
      setIsError(false);
    }, 3700);
  };

  const showSuccess = (successMessage) => {
    setIsError(false);
    setMsg(successMessage);
    setDialogBox(true);
    setTimeout(() => {
      setDialogBox(false);
    }, 5000);
  };

  // ✅ UPDATED: Enhanced updateOrder function with photo upload
  const updateOrder = async () => {
    const isCompletingOrder = orderStatus === "Completed" && orderData?.status !== "Completed";
    
    if ((!date && orderStatus !== "Pending") || !custId || productArr.length === 0) {
      showError("Please fill in all required fields ❗");
      return;
    }

    if (isCompletingOrder && !paymentType) {
      showError("Please select a payment type when completing an order ❗");
      return;
    }

    try {
      setLoading(true);
      
      // ✅ NEW: Create FormData for file upload
      const formData = new FormData();
      
      // Add order data as JSON
      const updateData = {
        customer: custId,
        date: date || null,
        products: [...productArr],
        scheduled,
        isPickup,
        purchaseOrdeNo: poc,
        comments: comment,
        status: orderStatus,
        paymentType: paymentType,
        paymentCollected: paymentCollected,
        uploadedBy: sessionStorage.getItem("email") || "User"
      };
      
      // Add each field to FormData
      Object.keys(updateData).forEach(key => {
        if (key === 'products') {
          formData.append(key, JSON.stringify(updateData[key]));
        } else {
          formData.append(key, updateData[key]);
        }
      });
      
      // ✅ NEW: Add photos to FormData
      if (selectedPhotos.length > 0) {
        setIsUploadingPhotos(true);
        selectedPhotos.forEach((photo, index) => {
          formData.append('photos', photo, photo.name);
        });
      }

      const res = await axiosInstance.patch(`order/${orderData._id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setLoading(false);
      setIsUploadingPhotos(false);

      // ✅ Enhanced success message handling
      const customerName = customers.find((c) => c._id === custId)?.businessName || "Customer";
      const orderType = isPickup ? "Pickup" : "Delivery";
      
      let successMessage = `${customerName} - ${orderType} order updated successfully!`;
      
      // Add status change info
      if (orderStatus !== orderData?.status) {
        successMessage += `\n\n📊 Status changed: ${orderData?.status} → ${orderStatus}`;
        
        if (isCompletingOrder) {
          successMessage += `\n💰 Payment: ${paymentType} (${paymentCollected ? 'Collected' : 'Not Collected'})`;
          successMessage += `\n📦 Stock has been reduced for this order`;
        }
      }
      
      // ✅ NEW: Add photo upload info
      if (res.data.photoUpload?.photosUploaded > 0) {
        successMessage += `\n\n📸 ${res.data.photoUpload.photosUploaded} photo(s) uploaded successfully`;
      }
      
      // Check if a duplicate was created for scheduled orders
      if (res.data.scheduledOrderDuplication?.duplicateCreated) {
        const duplicateOrderNumber = res.data.scheduledOrderDuplication.duplicateOrderNumber;
        successMessage += `\n\n🔄 Scheduled Order Completed!\nA duplicate order #${duplicateOrderNumber} has been automatically created for future scheduling.`;
      }

      // Add completion summary if order was completed
      if (res.data.completionSummary?.orderCompleted) {
        const syncSummary = res.data.completionSummary.wooCommerceSyncSummary;
        successMessage += `\n\n🌐 WooCommerce Sync: ${syncSummary.successful}/${syncSummary.total} products synced successfully`;
        
        if (syncSummary.failed > 0) {
          successMessage += `\n⚠️ ${syncSummary.failed} sync(s) failed - check with admin`;
        }
      }

      showSuccess(successMessage);
      
      // ✅ NEW: Clear photo selection after successful upload
      setSelectedPhotos([]);
      setPhotoPreview([]);
      
      // ✅ NEW: Update existing photos if new ones were uploaded
      if (res.data.photoUpload?.photoUrls) {
        setExistingPhotos([...existingPhotos, ...res.data.photoUpload.photoUrls]);
      }
      
    } catch (error) {
      setLoading(false);
      setIsUploadingPhotos(false);
      console.error("Order update error:", error);

      if (error.response && error.response.status >= 400 && error.response.status <= 500) {
        const errorMessage = error.response.data.error || error.response.data.message || "An error occurred";
        showError(errorMessage);
      } else {
        showError("Network error. Please check your connection and try again.");
      }
    }
  };

  const removeProduct = (id) => {
    setProductArr(productArr.filter((i) => i.product._id !== id));
  };

  const handleDate = (e) => {
    setDate(e.target.value);
  };

  const handlePriceChange = (productId, newPrice) => {
    const updatedPrice = newPrice === "" ? "" : Number(newPrice);

    setProductArr((prevProductArr) =>
      prevProductArr.map((product) =>
        product.product._id === productId
          ? {
              ...product,
              price: updatedPrice,
              total:
                product.quantity === ""
                  ? ""
                  : (Number(product.quantity) * updatedPrice).toFixed(2),
            }
          : product
      )
    );
  };

  const handleQuantityChange = (id, newQuantity) => {
    const quantityValue = newQuantity === "" ? "" : Number(newQuantity);

    setProductArr((prevProductArr) =>
      prevProductArr.map((product) =>
        product.product._id === id
          ? {
              ...product,
              quantity: quantityValue,
              total:
                quantityValue === ""
                  ? ""
                  : (quantityValue * product.price).toFixed(2),
            }
          : product
      )
    );
  };

  const addProducts = () => {
    if (!prodID.label) {
      return;
    }

    const prodId = prodID.label;
    const selectedProduct = products.find((product) => product._id === prodId);

    if (!selectedProduct) {
      return;
    }

    const price = Number(selectedProduct.price);
    const totalPrice = (Number(quantity) * price).toFixed(2);

    const existingProductIndex = productArr.findIndex((i) => i.product._id === prodId);

    if (existingProductIndex !== -1) {
      setProductArr((prevValue) =>
        prevValue.map((item, index) =>
          index === existingProductIndex
            ? {
                ...item,
                quantity:
                  quantity !== ""
                    ? Number(quantity) + Number(item.quantity)
                    : item.quantity,
                total:
                  quantity !== ""
                    ? ((Number(quantity) + Number(item.quantity)) * price).toFixed(2)
                    : item.total,
              }
            : item
        )
      );
    } else {
      setProductArr((prevValue) => [
        ...prevValue,
        { 
          product: selectedProduct, 
          quantity: Number(quantity), 
          price, 
          total: totalPrice 
        },
      ]);
    }

    setQuantity("");
    setProdId({});
  };

  const handleCheckbox = (e) => {
    setScheduled(e.target.checked);
  };

  const handlePickupCheckbox = (e) => {
    setIsPickup(e.target.checked);
  };

  const handleClose = () => {
    setDialogBox(false);
    setIsError(false);
  };

  // ✅ NEW: Cleanup function for photo previews
  useEffect(() => {
    return () => {
      photoPreview.forEach(preview => {
        if (preview.url && preview.isNew) {
          URL.revokeObjectURL(preview.url);
        }
      });
    };
  }, []);

  if (loading) {
    return (
      <div className={stylez.loaderContainer}>
        <LoadingSpinner />
        {isUploadingPhotos && (
          <p className={stylez.uploadingMessage}>
            📸 Processing and uploading photos...
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={stylez.container}>
      <h2 className={stylez.title}>
        Update Order #{orderData?.orderNumber}
      </h2>

      {/* ✅ Enhanced Order Status Badge */}
      <div className={stylez.orderStatusSection}>
        <div className={stylez.statusBadge}>
          <span className={`${stylez.status} ${stylez[orderData?.status?.toLowerCase()]}`}>
            Current: {orderData?.status}
          </span>
          {orderStatus !== orderData?.status && (
            <span className={`${stylez.status} ${stylez.changing}`}>
              → {orderStatus}
            </span>
          )}
          <span className={stylez.orderType}>
            {orderData?.isPickup ? "📦 Pickup Order" : "🚚 Delivery Order"}
          </span>
          {orderData?.scheduled && (
            <span className={stylez.scheduledBadge}>
              🔄 Scheduled
            </span>
          )}
          {/* ✅ NEW: Photo count badge */}
          {(existingPhotos.length > 0 || photoPreview.length > 0) && (
            <span 
              className={stylez.photoBadge}
              onClick={() => setShowPhotoGallery(true)}
              title="Click to view photos"
            >
              📸 {existingPhotos.length + photoPreview.length} photo{existingPhotos.length + photoPreview.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* ✅ NEW: Photo Gallery Modal */}
      {showPhotoGallery && <PhotoGalleryModal />}

      {/* Success/Error Dialog */}
      {dialogBox && (
        <div className={stylez.dialogOverlay}>
          <div
            className={`${stylez.dialogBox} ${
              isError ? stylez.errorDialog : stylez.successDialog
            }`}
          >
            <div className={stylez.dialogHeader}>
              <h3 className={stylez.dialogTitle}>
                {isError ? "❌ Update Failed" : "✅ Order Updated"}
              </h3>
              <button className={stylez.closeButton} onClick={handleClose}>
                ×
              </button>
            </div>
            <div className={stylez.dialogContent}>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{msg}</pre>
            </div>
            <div className={stylez.dialogFooter}>
              <button className={stylez.dialogCloseBtn} onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={stylez.orderForm}>
        {/* ✅ Order Basic Info */}
        <div className={stylez.orderBasicInfo}>
          <div className={stylez.formRow}>
            <div className={stylez.formGroup}>
              <label>Order Date:</label>
              <input
                type="date"
                value={date}
                onChange={handleDate}
                className={stylez.formInput}
                required={orderStatus !== "Pending"}
              />
              {orderStatus === "Pending" && !date && (
                <small className={stylez.helpText}>
                  Date can be set later for pending orders
                </small>
              )}
            </div>

            <div className={stylez.formGroup}>
              <label>Customer:</label>
              <Select
                required
                name="Customer"
                options={customerOptions}
                onChange={customerHandler}
                value={customerOptions?.find(option => option.value === custId)}
                placeholder="Select customer..."
                styles={selectStyles}
                isSearchable={true}
                isClearable={true}
                className={stylez.reactSelect}
                classNamePrefix="react-select"
              />
            </div>

            <div className={stylez.formGroup}>
              <label>Purchase Order Number:</label>
              <input
                type="text"
                name="poc"
                placeholder="Enter PO number (optional)"
                value={poc}
                onChange={(e) => setPoc(e.target.value)}
                className={stylez.formInput}
              />
            </div>
          </div>

          {/* ✅ Order Status Section */}
          <div className={stylez.orderStatusUpdateSection}>
            <h3 className={stylez.sectionTitle}>Order Status & Payment</h3>
            <div className={stylez.formRow}>
              <div className={stylez.formGroup}>
                <label>Order Status:</label>
                <Select
                  name="orderStatus"
                  options={statusOptions}
                  onChange={handleStatusChange}
                  value={statusOptions.find(option => option.value === orderStatus)}
                  placeholder="Select status..."
                  styles={getStatusSelectStyles(selectStyles)}
                  isSearchable={false}
                  className={stylez.reactSelect}
                  classNamePrefix="react-select"
                />
                {orderStatus === "Completed" && orderData?.status !== "Completed" && (
                  <small className={stylez.warningText}>
                    ⚠️ Completing this order will reduce stock levels
                  </small>
                )}
              </div>

              {(orderStatus === "Completed" || orderData?.status === "Completed") && (
                <>
                  <div className={stylez.formGroup}>
                    <label>Payment Type:</label>
                    <Select
                      name="paymentType"
                      options={paymentTypeOptions}
                      onChange={handlePaymentTypeChange}
                      value={paymentTypeOptions.find(option => option.value === paymentType)}
                      placeholder="Select payment type..."
                      styles={selectStyles}
                      isSearchable={false}
                      className={stylez.reactSelect}
                      classNamePrefix="react-select"
                    />
                  </div>

                  <div className={stylez.formGroup}>
                    <label className={stylez.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={paymentCollected}
                        onChange={(e) => setPaymentCollected(e.target.checked)}
                        className={stylez.checkbox}
                      />
                      <span className={stylez.checkboxText}>Payment Collected</span>
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ✅ Order Type Section */}
          <div className={stylez.orderTypeSection}>
            <h3 className={stylez.sectionTitle}>Order Type & Options</h3>
            <div className={stylez.checkboxRow}>
              <div className={stylez.checkboxGroup}>
                <label className={stylez.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={isPickup}
                    onChange={handlePickupCheckbox}
                    className={stylez.checkbox}
                  />
                  <span className={stylez.checkboxText}>📦 Pickup</span>
                </label>
                <small className={stylez.helpText}>
                  {isPickup
                    ? "Customer will collect order from warehouse"
                    : "Order will be delivered to customer"
                  }
                </small>
              </div>

              <div className={stylez.checkboxGroup}>
                <label className={stylez.checkboxLabel}>
                  <input
                    type="checkbox"
                    onChange={handleCheckbox}
                    checked={scheduled}
                    className={stylez.checkbox}
                  />
                  <span className={stylez.checkboxText}>📅 Scheduled Order</span>
                </label>
                <small className={stylez.helpText}>
                  {scheduled && orderStatus === "Completed" 
                    ? "⚠️ Completing will create a duplicate order for future scheduling"
                    : "Mark if this is a scheduled/recurring order"
                  }
                </small>
              </div>
            </div>
          </div>

          <div className={stylez.formGroup}>
            <label>Order Comments:</label>
            <textarea
              rows="4"
              placeholder="Comments about this order..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className={stylez.textarea}
            />
          </div>
        </div>

        {/* ✅ NEW: Photo Upload Section */}
        <div className={stylez.photoUploadSection}>
          <h3 className={stylez.sectionTitle}>📸 Order Photos</h3>
          
          {/* ✅ Current photos display */}
          {existingPhotos.length > 0 && (
            <div className={stylez.existingPhotosPreview}>
              <h4>Current Photos ({existingPhotos.length})</h4>
              <div className={stylez.photoPreviewGrid}>
                {existingPhotos.slice(0, 3).map((photoUrl, index) => (
                  <div key={index} className={stylez.photoPreviewItem}>
                    <img src={photoUrl} alt={`Order photo ${index + 1}`} />
                  </div>
                ))}
                {existingPhotos.length > 3 && (
                  <div 
                    className={stylez.morePhotosIndicator}
                    onClick={() => setShowPhotoGallery(true)}
                  >
                    <FaEye />
                    <span>+{existingPhotos.length - 3} more</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ✅ Photo upload interface */}
          <div className={stylez.photoUploadContainer}>
            <div className={stylez.fileInputContainer}>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoChange}
                className={stylez.fileInput}
                id="orderPhotoUpload"
                disabled={loading}
              />
              <label htmlFor="orderPhotoUpload" className={stylez.fileInputLabel}>
                <FaUpload className={stylez.uploadIcon} />
                Add Photos
              </label>
              <small className={stylez.helpText}>
                Add photos to document this order (max 10MB each). Photos will be watermarked with timestamp.
              </small>
            </div>

            {/* ✅ New photo preview */}
            {photoPreview.length > 0 && (
              <div className={stylez.newPhotosContainer}>
                <h4>📷 New Photos to Upload ({photoPreview.length})</h4>
                <div className={stylez.photoGrid}>
                  {photoPreview.map((preview, index) => (
                    <div key={index} className={stylez.newPhotoItem}>
                      <img 
                        src={preview.url} 
                        alt={`New photo ${index + 1}`}
                        className={stylez.newPhotoImage}
                      />
                      <div className={stylez.photoInfo}>
                        <span className={stylez.photoName} title={preview.name}>
                          {preview.name}
                        </span>
                        <span className={stylez.photoSize}>{preview.size}</span>
                        <button
                          type="button"
                          onClick={() => removeNewPhoto(index)}
                          className={stylez.removeNewPhotoBtn}
                          disabled={loading}
                          title="Remove this photo"
                        >
                          ❌
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Product Management */}
        <div className={stylez.productContainer}>
          <label>Add Product: </label>
          <Select
            required
            name="product"
            options={productOptions}
            onChange={productHandler}
            value={productOptions?.find(option => option.value === prodID.label)}
            placeholder="Select product..."
            styles={selectStyles}
            isSearchable={true}
            isClearable={true}
            className={stylez.reactSelect}
            classNamePrefix="react-select"
          />
          <input
            type="number"
            placeholder="Quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className={stylez.quantityInput}
          />
          <button className={stylez.addProductBtn} onClick={addProducts}>
            Add Product +
          </button>
        </div>

        {/* Products Table */}
        <div className={stylez.productsView}>
          <h4>Current Products in Order</h4>
          {productArr.length === 0 ? (
            <div className={stylez.emptyState}>
              No products in this order yet. Add some products above.
            </div>
          ) : (
            <table>
              <thead className={stylez.headers}>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Price(RRP)</th>
                  <th>Total</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {productArr?.map((item, index) => {
                  const product = item.product;
                  return (
                    <tr className={stylez.products} key={index}>
                      <td>{product?.productName}</td>
                      <td>{product?.sku}</td>
                      <td>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) =>
                            handleQuantityChange(product._id, e.target.value)
                          }
                          className={stylez.tableInput}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onChange={(e) =>
                            handlePriceChange(product._id, e.target.value)
                          }
                          value={item.price}
                          className={stylez.tableInput}
                        />
                      </td>
                      <td>${product?.price}</td>
                      <td>${item.total}</td>
                      <td>
                        <button
                          className={stylez.icon}
                          onClick={() => removeProduct(product._id)}
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ✅ Enhanced Submit Button */}
        <div className={stylez.actionButtons}>
          <button className="confirmBtn" onClick={updateOrder} disabled={loading}>
            {loading 
              ? isUploadingPhotos
                ? "Uploading Photos..."
                : "Updating..." 
              : orderStatus === "Completed" && orderData?.status !== "Completed"
              ? `Complete ${isPickup ? "Pickup" : "Delivery"} Order`
              : `Update ${isPickup ? "Pickup" : "Delivery"} Order`
            }
          </button>
          
          {orderStatus === "Completed" && orderData?.status !== "Completed" && (
            <div className={stylez.completionWarning}>
              <small>
                ⚠️ This will reduce stock levels and sync with WooCommerce
                {scheduled && (
                  <><br />🔄 A duplicate order will be created for future scheduling</>
                )}
                {selectedPhotos.length > 0 && (
                  <><br />📸 {selectedPhotos.length} photo(s) will be uploaded</>
                )}
              </small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateOrder;
